import { useState, useEffect } from 'react'
import Editor, { DiffEditor } from '@monaco-editor/react'
import { useWorkflowYamlSnapshots } from '@/hooks'
import { formatDateTime } from '@/lib/timeFormatters'
import type { WorkflowYamlSnapshot } from '@/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

interface WorkflowYamlSnapshotHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  definitionId: string
  currentYamlSnapshotId: string | null
  currentEditorYaml: string
}

type ViewMode = 'view' | 'compare'

export function WorkflowYamlSnapshotHistoryDialog({
  open,
  onOpenChange,
  projectId,
  definitionId,
  currentYamlSnapshotId,
  currentEditorYaml,
}: WorkflowYamlSnapshotHistoryDialogProps) {
  const { data: snapshots, isLoading, isError, refetch } = useWorkflowYamlSnapshots(projectId, open ? definitionId : undefined)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('view')

  useEffect(() => {
    if (snapshots && snapshots.length > 0 && !selectedId) {
      const current = snapshots.find(s => s.id === currentYamlSnapshotId)
      setSelectedId(current?.id ?? snapshots[0].id)
    }
  }, [snapshots, currentYamlSnapshotId])

  useEffect(() => {
    if (!open) {
      setSelectedId(null)
      setViewMode('view')
    }
  }, [open])

  const selected = snapshots?.find(s => s.id === selectedId) ?? null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="shrink-0 px-6 py-4 border-b">
          <DialogTitle>YAML Version History</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          <div className="w-64 shrink-0 border-r flex flex-col overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center flex-1 text-sm text-muted-foreground">
                Loading...
              </div>
            )}
            {isError && (
              <div
                role="alert"
                data-testid="yaml-snapshot-history-error"
                className="flex flex-col items-center justify-center flex-1 gap-2 text-sm text-destructive text-center px-4"
              >
                <span>Failed to load version history.</span>
                <button className="underline" onClick={() => refetch()}>Retry</button>
              </div>
            )}
            {!isLoading && !isError && (!snapshots || snapshots.length === 0) && (
              <div className="flex items-center justify-center flex-1 text-sm text-muted-foreground">
                No versions yet
              </div>
            )}
            {snapshots?.map(snapshot => (
              <SnapshotListItem
                key={snapshot.id}
                snapshot={snapshot}
                isCurrent={snapshot.id === currentYamlSnapshotId}
                isSelected={snapshot.id === selectedId}
                onClick={() => setSelectedId(snapshot.id)}
              />
            ))}
          </div>

          <div className="flex-1 min-w-0 flex flex-col">
            <div className="shrink-0 flex items-center gap-1 px-4 py-2 border-b">
              <Button
                variant={viewMode === 'view' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('view')}
                className="h-7 px-3 text-xs"
              >
                View
              </Button>
              <Button
                variant={viewMode === 'compare' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('compare')}
                className="h-7 px-3 text-xs"
              >
                Compare with current
              </Button>
            </div>

            <div className="flex-1 min-h-0">
              {!selected ? (
                <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                  Select a version to view
                </div>
              ) : viewMode === 'view' ? (
                <Editor
                  height="100%"
                  language="yaml"
                  theme="vs-dark"
                  value={selected.yamlContent}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineNumbers: 'on',
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    folding: true,
                    bracketPairColorization: { enabled: true },
                  }}
                />
              ) : (
                <DiffEditor
                  height="100%"
                  language="yaml"
                  theme="vs-dark"
                  original={selected.yamlContent}
                  modified={currentEditorYaml}
                  options={{
                    readOnly: true,
                    renderSideBySide: true,
                    minimap: { enabled: false },
                    fontSize: 14,
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface SnapshotListItemProps {
  snapshot: WorkflowYamlSnapshot
  isCurrent: boolean
  isSelected: boolean
  onClick: () => void
}

function SnapshotListItem({ snapshot, isCurrent, isSelected, onClick }: SnapshotListItemProps) {
  return (
    <button
      type="button"
      data-testid={`snapshot-${snapshot.id}`}
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 border-b hover:bg-muted/50 transition-colors',
        isSelected && 'bg-muted',
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-medium truncate">{snapshot.version}</span>
        {isCurrent && (
          <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
            Current
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{formatDateTime(snapshot.createdAt)}</p>
      <p className="text-xs text-muted-foreground font-mono">{snapshot.contentHash.slice(0, 8)}</p>
    </button>
  )
}
